/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import {
    Badge,
    Heading,
    ReadMoreBlock,
    Text,
    useBreakpoint,
    ExternalLink,
    ContentContainerBodyShift,
} from 'sarsaparilla';

import './DigitalIAP.scss';

const digitalIAPContentBody = (
    <>
        <p>
            America the Beautiful Passes cover entrance or standard amenity{' '}
            <ExternalLink url="https://store.usgs.gov/faq">(day-use) fees</ExternalLink>{' '}
            to thousands of recreation areas managed by six Federal agencies:
        </p>
        <ul className="agencies-list">
            <li>Forest Service</li>
            <li>National Park Service</li>
            <li>U.S. Fish and Wildlife Service</li>
            <li>Bureau of Land Management</li>
            <li>Bureau of Reclamation</li>
            <li>U.S. Army Corps of Engineers</li>
        </ul>
        <p>
            The America the Beautiful Pass does not cover expanded amenity fees such as
            camping, parking, special tours, special permits, or ferries. Senior and
            Access Passes may offer a discount on some expanded amenity fees (e.g.
            camping). Lands managed by concessioners may not accept America the Beautiful
            Passes. (Concessioners are private companies granted permission by the
            agencies to provide services.)
        </p>
        <p>
            <strong>
                Each America the Beautiful Pass is issued to one person (the pass holder)
                who must be present, with valid photo identification, each time the pass
                is used.
            </strong>
        </p>
        <ul className="usage-list">
            <li>
                For locations or areas that charge fees <strong>per vehicle</strong>, an
                America the Beautiful Pass admits the pass holder and passengers in one
                non-commercial vehicle, or up to two motorcycles.
            </li>
            <li>
                For locations or areas that charge fees <strong>per person</strong>, an
                America the Beautiful Pass admits the pass holder plus 3 adults (16 and
                older), not to exceed 4 adults.
            </li>
        </ul>
        <p>
            What&apos;s new in 2026 is the ability to get digital versions of these
            passes, as required by Congress (2025 EXPLORE Act).
        </p>
        <p>
            <strong>Digital passes may not be purchased as gifts.</strong>
        </p>

        <p>
            There are several ways to get a physical pass if you want. For all except the
            Non-Resident Annual Pass and the 4th Grade Pass: choose the “physical pass”
            option before you finalize your purchase in the cart to have a pass delivered
            by mail ($7.50 shipping and handling fee applies). Or, you can get a physical
            pass in person at a site near you (see{' '}
            <ExternalLink url="https://www.nps.gov/planyourvisit/pickup-pass-locations.htm">
                list of locations
            </ExternalLink>
            ) or by visiting{' '}
            <ExternalLink url="https://store.usgs.gov/">store.usgs.gov</ExternalLink>.
        </p>
    </>
);

function DigitalIAP() {
    const { isLgAndUp } = useBreakpoint();

    return (
        <ContentContainerBodyShift className="my-6 ">
            <section
                aria-label="Digital Interagency Pass"
                id="digital-interagency-passes"
            >
                <div className="badge-and-header-group">
                    <Badge className="new-badge">NEW</Badge>
                    <Heading
                        headingLevel={2}
                        hasUnderline="left"
                        appearance="h4"
                        className="header"
                        id="digital-recommended-for"
                    >
                        Digital America the Beautiful Passes
                    </Heading>
                </div>
                <div className="recommended-for">
                    <Text size="lg" fontWeight="semibold">
                        Recommended for:
                    </Text>
                    <ul aria-labelledby="digital-recommended-for">
                        <li>One or more visits to one or multiple locations</li>
                        <li>Seniors, military, visitors with disabilities</li>
                        <li>4th grade students</li>
                    </ul>
                </div>
                <div className="digital-iap-description-section">
                    {isLgAndUp ? (
                        digitalIAPContentBody
                    ) : (
                        <ReadMoreBlock id="digital-iap-read-more" linesToShow={4}>
                            {digitalIAPContentBody}
                        </ReadMoreBlock>
                    )}
                </div>
            </section>
        </ContentContainerBodyShift>
    );
}

export default DigitalIAP;
